// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-statements */
import { useEffect, useState } from 'react';

type ScriptStatus = 'idle' | 'loading' | 'ready' | 'error';

interface UseScriptOptions {
  onLoad?: () => void;
  onError?: () => void;
  async?: boolean;
  defer?: boolean;
  removeOnUnmount?: boolean;
}

export const useScriptLoader = (src: string | null, options: UseScriptOptions = {}): ScriptStatus => {
  const { onLoad, onError, async = true, defer = false, removeOnUnmount = false } = options;

  const [status, setStatus] = useState<ScriptStatus>(src ? 'loading' : 'idle');

  useEffect(() => {
    if (!src) {
      setStatus('idle');
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

    if (existingScript) {
      const existingStatus = existingScript.getAttribute('data-script-status') as ScriptStatus;

      if (existingStatus === 'ready') {
        setStatus('ready');
        onLoad?.();
        return;
      }
      if (existingStatus === 'error') {
        setStatus('error');
        onError?.();
        return;
      }

      setStatus('loading');
      return;
    }

    setStatus('loading');

    const script = document.createElement('script');
    script.src = src;
    script.async = async;
    script.defer = defer;
    script.setAttribute('data-script-status', 'loading');

    const handleLoad = () => {
      setStatus('ready');
      script.setAttribute('data-script-status', 'ready');
      onLoad?.();
    };

    const handleError = () => {
      setStatus('error');
      script.setAttribute('data-script-status', 'error');
      onError?.();
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    document.body.appendChild(script);

    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);

      if (removeOnUnmount && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [src, onLoad, onError, async, defer, removeOnUnmount]);

  return status;
};
